import React, { useState, useEffect, useRef } from "react";
import myImg from "../../Assets/jen2.jpg";

const IDBadge = () => {
    const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });

    // Using refs for physics to avoid continuous re-renders overriding state closures
    const rotRef = useRef({ x: 0, y: 0, z: 0 });
    const velRef = useRef({ x: 0, y: 0, z: 0 });

    const lastScrollY = useRef(0);
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });

    useEffect(() => {
        lastScrollY.current = window.scrollY;

        let animationFrameId;
        const updatePhysics = () => {
            if (!isDragging.current) {
                // Friction / Damping - gradually slows down the swing
                velRef.current.x *= 0.92;
                velRef.current.y *= 0.95;
                velRef.current.z *= 0.92;

                // Gravity / Restoring force bringing it back to 0 rotation
                velRef.current.x += -rotRef.current.x * 0.04;
                velRef.current.y += -rotRef.current.y * 0.04;
                velRef.current.z += -rotRef.current.z * 0.04;

                // Apply scroll delta to X velocity (swing forward/back based on scroll up/down)
                const currentScrollY = window.scrollY;
                const scrollDelta = currentScrollY - lastScrollY.current;
                lastScrollY.current = currentScrollY;

                if (Math.abs(scrollDelta) > 0) {
                    velRef.current.x -= scrollDelta * 0.05;
                }

                // Apply the calculated velocities to the current rotation
                rotRef.current.x += velRef.current.x;
                rotRef.current.y += velRef.current.y;
                rotRef.current.z += velRef.current.z;

                // Clamp rotations to prevent physics breaking or flipping infinitely
                rotRef.current.x = Math.max(-60, Math.min(60, rotRef.current.x));
                rotRef.current.z = Math.max(-60, Math.min(60, rotRef.current.z));
                rotRef.current.y = Math.max(-180, Math.min(180, rotRef.current.y));
            }

            // Schedule React state update to re-render component
            setRotation({ ...rotRef.current });
            animationFrameId = requestAnimationFrame(updatePhysics);
        };

        updatePhysics();

        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    const handlePointerDown = (e) => {
        isDragging.current = true;
        dragStart.current = { x: e.clientX || (e.touches && e.touches[0].clientX), y: e.clientY || (e.touches && e.touches[0].clientY) };
    };

    const handlePointerMove = (e) => {
        if (isDragging.current) {
            const clientX = e.clientX || (e.touches && e.touches[0].clientX);
            const clientY = e.clientY || (e.touches && e.touches[0].clientY);

            const deltaX = clientX - dragStart.current.x;
            const deltaY = clientY - dragStart.current.y;

            rotRef.current.z -= deltaX * 0.5; // Dragging horizontally swings Z axis
            rotRef.current.x += deltaY * 0.5; // Dragging vertically tips X axis
            rotRef.current.y -= deltaX * 0.3; // Add a twist when moving side to side

            dragStart.current = { x: clientX, y: clientY };
        }
    };

    const handlePointerUp = () => {
        isDragging.current = false;
    };

    const handleClick = () => {
        // Add a randomized physics impulse when tapped without dragging
        if (!isDragging.current && Math.abs(velRef.current.z) < 5 && Math.abs(velRef.current.x) < 5) {
            velRef.current.z += (Math.random() > 0.5 ? 1 : -1) * 25;
            velRef.current.x += (Math.random() > 0.5 ? 1 : -1) * 20;
            velRef.current.y += (Math.random() > 0.5 ? 1 : -1) * 40;
        }
    };

    return (
        <div
            className="relative flex flex-col items-center justify-start h-[550px] w-full max-w-[400px] overflow-visible select-none"
            style={{ perspective: '1200px' }}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
        >
            {/* Lanyard / Lace */}
            <div
                className="absolute left-1/2 -translate-x-1/2 w-6 z-10 drop-shadow-lg"
                style={{
                    top: "-1000px",
                    height: "1080px",
                    background: "repeating-linear-gradient(45deg, #18181b, #18181b 10px, #27272a 10px, #27272a 20px)",
                    borderLeft: "2px solid #3f3f46",
                    borderRight: "2px solid #000"
                }}
            ></div>

            {/* Swaying Card Container */}
            <div
                className="relative mt-[80px] z-20 cursor-grab active:cursor-grabbing w-[280px] group"
                style={{
                    transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) rotateZ(${rotation.z}deg)`,
                    transformOrigin: '50% -18px',
                    transformStyle: 'preserve-3d',
                    // Only apply transition if we aren't actively fighting it with pointers
                    transition: isDragging.current ? 'none' : 'transform 0.05s linear'
                }}
                onMouseDown={handlePointerDown}
                onTouchStart={handlePointerDown}
                onClick={handleClick}
            >
                {/* Metal Hanging Clip */}
                <div className="absolute -top-[18px] left-1/2 -translate-x-1/2 w-8 h-[24px] bg-gradient-to-b from-zinc-300 to-zinc-500 rounded-sm border-[3px] border-zinc-500 shadow-md flex items-end justify-center pb-1 z-30">
                    <div className="w-3 h-3 border-2 border-zinc-600 rounded-full bg-transparent"></div>
                </div>

                {/* The Card Element */}
                <div className="w-full bg-white rounded-[20px] shadow-2xl flex flex-col items-center p-6 border-t-[12px] border-t-black overflow-hidden relative">
                    {/* Lanyard Clip Cutout Graphic */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-[6px] bg-black/10 rounded-b-full shadow-inner z-0"></div>

                    {/* Faint dot pattern inside card */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, black 1px, transparent 0)', backgroundSize: '16px 16px' }}></div>

                    <h2 className="text-black font-black text-2xl tracking-tighter mt-4 mb-2 uppercase text-center w-full border-b-[3px] border-zinc-100 pb-2">EDITOR ID</h2>

                    {/* Profile Avatar */}
                    <div className="w-40 h-40 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.2)] overflow-hidden my-4 relative z-10 border-4 border-white ring-2 ring-zinc-100 bg-white">
                        <img src={myImg} alt="ID Avatar" className="w-full h-full object-cover object-top" draggable="false" />
                    </div>

                    {/* Personal Details */}
                    <h3 className="text-black font-black text-[22px] tracking-tight uppercase leading-none mt-2">Jenemae</h3>
                    <h3 className="text-black font-medium text-lg tracking-tight uppercase"> Labastida</h3>

                    <p className="text-white bg-black px-4 py-[6px] rounded-full font-bold tracking-widest text-[11px] mt-4 uppercase mb-6 shadow-xl relative z-10">Creative Editor</p>

                    {/* Footer Barcode Element */}
                    <div className="mt-auto w-full pt-4 border-t-4 border-black flex justify-between items-end relative z-10">
                        <div>
                            <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest mb-1">ID Number</p>
                            <p className="text-[13px] text-black font-black font-mono tracking-wider">JL-1221-2001</p>
                        </div>
                        {/* Visual Decorative Barcode */}
                        <div className="flex gap-[3px] h-8 opacity-90">
                            <span className="w-[3px] bg-black h-full"></span>
                            <span className="w-[1px] bg-black h-full"></span>
                            <span className="w-[4px] bg-black h-full"></span>
                            <span className="w-[2px] bg-black h-[80%]"></span>
                            <span className="w-[1px] bg-black h-full"></span>
                            <span className="w-[3px] bg-black h-full"></span>
                            <span className="w-[2px] bg-black h-[90%]"></span>
                        </div>
                    </div>
                </div>

                {/* Glossy Overlay Reflection layer */}
                <div className="absolute inset-0 w-full h-full rounded-[20px] pointer-events-none overflow-hidden mix-blend-overlay opacity-50 z-50 group-hover:opacity-70 transition-opacity">
                    <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-gradient-to-br from-white/90 via-white/5 to-transparent transform -rotate-45 translate-y-[-10%] translate-x-[-10%]"></div>
                </div>
            </div>
        </div>
    );
};

export default IDBadge;
